import { IProcessedStyleSet } from "@fluentui/react/lib/Styling"
import { classNamesFunction, styled } from "@fluentui/react/lib/Utilities"
import React, {
    CSSProperties,
    MouseEventHandler,
    MutableRefObject,
    ReactElement,
    ReactNode
} from "react"

import { Orientation } from "../../index"
import { BorderLayoutStyles } from "./BorderLayout.styles"
import {
    BorderLayoutProps,
    IBorderLayoutStyles,
    ResizeProps
} from "./BorderLayout.types"

const Resize = (props: ResizeProps) => {
    if (props.disabled === true) {
        return null
    }

    const pos: MutableRefObject<number> = React.useRef(0)
    const axis = props.axis === "x" ? "x" : "y"
    const attribute = props.axis === "x" ? "width" : "height"

    const updateSizeStyle = (e: HTMLDivElement | null, delta: number) => {
        if (e !== null) {
            const size = parseInt(getComputedStyle(e, "")[attribute]) + delta
            e.style[attribute] = `${size}px`
        }
    }

    const resizeHandler = (event: MouseEvent) => {
        const newPos = event[axis]
        const delta = props.dir * (pos.current - newPos)
        updateSizeStyle(props.target.current, delta)
        pos.current = newPos
        window.dispatchEvent(new Event("resize"))
    }

    const onMouseDownHandler: MouseEventHandler<HTMLDivElement> = (
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        pos.current = event.nativeEvent[axis]

        document.addEventListener("mousemove", resizeHandler, false)
        document.addEventListener(
            "mouseup",
            () =>
                document.removeEventListener("mousemove", resizeHandler, false),
            false
        )
    }
    return <div className={props.className} onMouseDown={onMouseDownHandler} />
}

const isReactElement = (object: any): object is ReactElement => {
    return "key" in object
}

const hasKey = (node: ReactNode, name: string) => {
    if (node && isReactElement(node)) {
        return node.key === name
    }
    return false
}

/**
 * A layout container, which arranges and resizes its components to fit in the five regions "north", "east", "south", "west" and "center".
 */
const IBorderLayout = (props: BorderLayoutProps) => {
    const _north = React.createRef<HTMLDivElement>()
    const _south = React.createRef<HTMLDivElement>()
    const _east = React.createRef<HTMLDivElement>()
    const _west = React.createRef<HTMLDivElement>()

    const child = (name: string) => {
        if (props.children) {
            const children = Array.isArray(props.children)
                ? props.children
                : [props.children]
            return children.filter((current) => hasKey(current, name))
        }
    }

    const isShown = (border: string) => {
        return !Array.isArray(props.hidden) || props.hidden.indexOf(border) < 0
    }
    const renderBorder = (border: string) => {
        return props.layout
            .filter((item) => item.position === border)
            .map((item) => /* item.component || */ child(item.name))
    }
    const canResize = (border: string) => {
        if (
            isShown(border) &&
            (!Array.isArray(props.resize) ||
                props.resize.indexOf(border) === -1)
        ) {
            return false
        }
        return (
            props.layout
                .filter((item) => item.position === border)
                .map((item) => /* item.component || */ child(item.name))
                .filter(Boolean).length > 0
        )
    }

    const mergeStyles = (main: object, secondary?: any) => {
        return {
            ...main,
            ...((secondary as object) || {})
        }
    }

    const getStyle: (border: string) => CSSProperties | undefined = (
        border
    ) => {
        if (!isShown(border)) {
            return {
                display: "none"
            }
        }
        switch (border) {
            case Orientation.North:
                return mergeStyles(
                    { display: "flex", flexDirection: "row", minWidth: 0 },
                    props.borderStyles?.north
                )
            case Orientation.West:
                return mergeStyles(
                    { display: "flex", flexDirection: "column" },
                    props.borderStyles?.west
                )
            case Orientation.Center:
                return mergeStyles(
                    { display: "flex", flex: 1, flexDirection: "column" },
                    props.borderStyles?.center
                )
            case Orientation.East:
                return mergeStyles(
                    { display: "flex", flexDirection: "column" },
                    props.borderStyles?.east
                )
            case Orientation.South:
                return mergeStyles(
                    { display: "flex", flexDirection: "row", minWidth: 0 },
                    props.borderStyles?.south as object
                )
        }
    }
    const { styles, theme } = props
    const classNames = classNamesFunction()(
        styles,
        theme
    ) as IProcessedStyleSet<IBorderLayoutStyles>
    return (
        <div
            style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
            }}
            className={props.className}
        >
            <div
                ref={_north}
                data-border={Orientation.North}
                style={getStyle(Orientation.North)}
            >
                {renderBorder(Orientation.North)}
            </div>
            <Resize
                disabled={!canResize(Orientation.North)}
                target={_north}
                dir={-1}
                axis="y"
                className={classNames.horizontal}
            />
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "row",
                    overflow: "hidden"
                }}
            >
                <div
                    ref={_west}
                    data-border={Orientation.West}
                    style={getStyle(Orientation.West)}
                >
                    {renderBorder(Orientation.West)}
                </div>
                {isShown(Orientation.West) && (
                    <Resize
                        disabled={!canResize(Orientation.West)}
                        target={_west}
                        dir={-1}
                        axis="x"
                        className={classNames.vertical}
                    />
                )}
                <div
                    data-border={Orientation.Center}
                    style={getStyle(Orientation.Center)}
                >
                    {renderBorder(Orientation.Center)}
                </div>
                <Resize
                    disabled={!canResize(Orientation.East)}
                    target={_east}
                    dir={1}
                    axis="x"
                    className={classNames.vertical}
                />
                <div
                    ref={_east}
                    data-border={Orientation.East}
                    style={getStyle(Orientation.East)}
                >
                    {renderBorder(Orientation.East)}
                </div>
            </div>
            <Resize
                disabled={!canResize(Orientation.South)}
                target={_south}
                dir={1}
                axis="y"
                className={classNames.horizontal}
            />
            <div
                ref={_south}
                data-border={Orientation.South}
                style={getStyle(Orientation.South)}
            >
                {renderBorder(Orientation.South)}
            </div>
        </div>
    )
}

export const BorderLayout = styled(IBorderLayout, BorderLayoutStyles)
BorderLayout.displayName = "BorderLayout"
